import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useServiceAuth from 'auth/useServiceAuth';
import { useMutation } from '@tanstack/react-query';
import { setEmailPreferences } from 'apis/adminAPIs';
import { setSendAllEmails } from 'state/slices/userSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

const roleLabel = {
  '["DEPARTMENT"]': 'Department',
  '["COLLEGE"]': 'College',
  '["PROVOST"]': 'Provost',
  '["DARS"]': 'DARS',
  '["VIEWER"]': 'Viewer - Viewers have view-only access to all checksheets.',
  '["PRVVIEWER"]':
    'PRV Viewer - PRV Viewers have view-only access to all checksheets.',
};



const host = process.env.REACT_APP_SERVICEAUTH_REDIRECT_URI;
console.log("host:", host);
const isDevEnv =
  host?.includes('dev') ||
  host?.includes('sandbox') ||
  host?.includes('localhost');

const Profile = () => {
  const dispatch = useDispatch();
  const { getAccessToken } = useServiceAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const asurite = useSelector((state) => state.user.asurite);
  const role = useSelector((state) => state.user.role);
  const college = useSelector((state) => state.user.college);
  const department = useSelector((state) => state.user.department);
  const detailsString = useSelector((state) => state.user.details);
  const sendAllEmails = useSelector((state) => state.user.sendAllEmails);
  const collegeList = useSelector((state) => state.dataLists.collegeList);
  const departmentList = useSelector((state) => state.dataLists.departmentList);

  const details = useMemo(
    () =>
      !!detailsString && detailsString !== 'undefined'
        ? JSON.parse(detailsString)
        : null,
    [detailsString]
  );

  const userCollege = useMemo(
    () =>
      !!college
        ? college.includes('[')
          ? JSON.parse(college)
          : college !== 'undefined' && [college]
        : null,
    [college]
  );

  const userDepartment = useMemo(
    () =>
      !!department
        ? department.includes('[')
          ? JSON.parse(department)
          : department !== 'undefined' && [department]
        : null,
    [department]
  );

  const { mutate: setEmail } = useMutation({
    mutationFn: setEmailPreferences,
    onSuccess: (data, variables) => {
      dispatch(setSendAllEmails(variables.jsonData.sendAllEmails));
      localStorage.setItem(
        'email-preference',
        variables.jsonData.sendAllEmails
      );
      setLoading(false);
    },
    onError: (error) => {
      setError(error);
      setLoading(false);
    },
  });

  const handleChangeEmailPreference = async (e) => {
    const { checked } = e.target;
    setLoading(true);
    const token = await getAccessToken();

    const jsonData = {
      sendAllEmails: checked ? 'Y' : 'N',
    };

    setEmail({
      jsonData,
      token,
    });
  };

  return (
    <div className="container pt-3">
      <div>
        {/* <h2 className="mb-6">
          {details?.name}{' '}
          <span className="fw-normal fs-3 fst-italic text-gray-5 ms-2">
            ( {asurite} )
          </span>
          <div>
            {asurite}
          </div>
        </h2> */}

        <div className="profile-title mb-4">
          <h2>{details?.name} </h2>
          <div className="mb-4 text-gray-5">{asurite}</div>
        </div>

        <div className="mb-6">
          <h3 className="mb-1">Role</h3>
          <p>{!!role && roleLabel[role]}</p>
        </div>
        {!role?.includes('VIEWER') && (
          <>
            {!!userCollege.length && !!collegeList && (
              <div className="mb-6">
                <h3 className="mb-1">College</h3>

                {userCollege.map((coll) => (
                  <div>{collegeList[coll]?.description}</div>
                ))}
              </div>
            )}
            {!!userDepartment.length && !!departmentList && (
              <div className="mb-6">
                <h3 className="mb-1">Department</h3>
                {userDepartment.map((dept) => (
                  <div>{departmentList[dept]?.description}</div>
                ))}
              </div>
            )}
            <div style={{ width: '35%' }}>
              <h3>Email preferences</h3>
              <div className="d-flex gap-2 justify-content-between align-items-center mb-2">
                <p className="fw-bold m-0">Receive email notifications</p>
                <div className="d-flex gap-2 align-items-center">
                  {loading && <FontAwesomeIcon icon={faSpinner} spin />}
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input toggle-switch"
                      type="checkbox"
                      id="emailPreference"
                      checked={
                        (isDevEnv && sendAllEmails === 'Y') ||
                        (!isDevEnv && sendAllEmails !== 'N')
                      }
                      onChange={(e) => handleChangeEmailPreference(e)}
                      disabled={loading}
                    />
                    <label
                      className="form-check-label fw-normal text-end"
                      style={{ width: '24px' }}
                      htmlFor="emailPreference"
                    >
                      {(isDevEnv && sendAllEmails === 'Y') ||
                      (!isDevEnv && sendAllEmails !== 'N')
                        ? 'On'
                        : 'Off'}
                    </label>
                  </div>
                </div>
              </div>
              <div style={{ width: '80%' }}>
                <p>
                  Turn on email notifications to get updates on programs at your
                  review level.
                </p>
                <p>Emails will be sent to your ASU email address.</p>
                {error && (
                  <div className="alert alert-danger" role="alert">
                    An error was encountered when retrieving preference
                    information. Please refresh and try again.
                    <br />
                    <br />
                    Error: {error}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Profile;
